import { FC } from 'react';
import clsx from 'clsx';
import { useUserDetailsStore } from '@marriott/mi-store-utils';
import { EnrollmentCardContainerProps, EnrollmentCardIcon } from './EnrollmentCardContainer.types';
import { EnrollmentCard } from '../../molecules/EnrollmentCard';
import { Amenity } from '../../molecules/EnrollmentCard/Amenity';

function buttonTarget(openInaNewTab?: boolean): '_blank' | '_self' {
  return openInaNewTab ? '_blank' : '_self';
}

function extractIconsFromProps<Props>(props: Record<string, Props>): EnrollmentCardIcon[] {
  const icons: EnrollmentCardIcon[] = [];
  const pathKeys = Object.keys(props).filter(key => key.startsWith('iconPath_'));

  pathKeys.forEach(pathKey => {
    const index = pathKey.split('_')[1];
    const icon: EnrollmentCardIcon = {
      path: (props[`iconPath_${index}`] as string) || '',
      altText: (props[`iconAltText_${index}`] as string) || '',
      iconOrder: [
        props[`iconPath_1`] as string,
        props[`iconPath_2`] as string,
        props[`iconPath_3`] as string,
        props[`iconPath_4`] as string,
      ],
    };
    icons.push(icon);
  });
  return icons;
}

export const EnrollmentCardContainerComp: FC<EnrollmentCardContainerProps & { className?: string; theme: string }> = ({
  className,
  header,
  primaryCtaLinkText,
  primaryCtaLink,
  primaryCtaOpenInNewTab,
  secondaryCtaLinkText,
  secondaryCtaLink,
  secondaryCtaOpenInNewTab,
  enrollmentCardDescription,
  altText,
  dynamicMedia,
  fileReferenceImage,
  styleclass,
  theme,
  ...rest
}) => {
  const isSignedInUser = useUserDetailsStore(state => state.isSignedInUser);
  const loggedInVariant = isSignedInUser;
  const loggedOutVariant = !loggedInVariant;

  const buttons = [
    {
      buttonCopy: primaryCtaLinkText,
      className: `m-button-m m-button-primary${loggedInVariant && '-inverse'}`,
      href: primaryCtaLink,
      target: buttonTarget(primaryCtaOpenInNewTab),
      ariaLabel: primaryCtaLinkText,
    },
  ];

  const icons = extractIconsFromProps(rest);
  const image = loggedOutVariant
    ? undefined
    : {
        altText,
        dynamicMedia,
        fileReferenceImage,
      };

  if (loggedOutVariant && secondaryCtaLinkText) {
    buttons.push({
      buttonCopy: secondaryCtaLinkText,
      className: 'm-button-m m-button-secondary',
      href: secondaryCtaLink,
      target: buttonTarget(secondaryCtaOpenInNewTab),
      ariaLabel: secondaryCtaLinkText,
    });
  }
  return (
    <EnrollmentCard
      className={clsx(className, styleclass ?? '', theme)}
      buttons={buttons}
      header={header}
      description={enrollmentCardDescription}
      isLoggedInVariant={loggedInVariant}
      image={image}
      theme={theme}
    >
      {loggedOutVariant &&
        icons.map(({ path, altText }) => {
          const [iconCategory, iconClass] = (path ? path[0] : '').split(':');
          const updatedIconClass = iconClass?.split('/').pop();
          const iconClassName = updatedIconClass || iconCategory;
          return <Amenity key={path + altText} iconClass={iconClassName} label={altText} />;
        })}
    </EnrollmentCard>
  );
};
